import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Container, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import PublicSpaceProfileHeader from '../../components/PublicSpaceProfileHeader';
import PublicSpaceRoomList from '../../components/PublicSpaceRoomList';
import PublicTutorsRoomPanel from '../../components/PublicTutorsRoomPanel';
import SEO from '../../components/SEO';
import TutorBookingStepperDialog from '../../components/TutorBookingStepperDialog';
import type { TutorSearchResult } from '../../models/Tutor';
import { type OrgTutorsPageResolution, useOrgTutorsPageData } from '../../hooks/useOrgTutorsPageData';
import { tutorListingPath } from '../../utils/searchPaths';
import { flattenOrganizationRooms } from '../../utils/publicOrgRooms';
import { publicSpacePagePath } from '../../utils/publicSpaceResolution';
import { trackStartBooking, trackViewProvider } from '../../utils/analytics';

export interface OrgTutorsPageShellProps {
  resolution: OrgTutorsPageResolution;
}

const OrgTutorsPageShell: React.FC<OrgTutorsPageShellProps> = ({ resolution }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    organization,
    spaces,
    tutors,
    loading,
    organizationError,
    tutorError,
    tutorReviewsById,
    tutorReviewsLoadingById,
    loadTutorReviews,
  } = useOrgTutorsPageData(resolution);
  const [selectedTutor, setSelectedTutor] = useState<TutorSearchResult | null>(null);
  const [selectedLessonOptionId, setSelectedLessonOptionId] = useState<string | null>(null);

  const currentPath = `${location.pathname}${location.search}`;

  const openTutorProfile = (tutor: TutorSearchResult) => {
    navigate(tutorListingPath(tutor.tutorId), {
      state: {
        tutor,
        searchBackPath: currentPath,
      },
    });
  };

  const openBooking = (tutor: TutorSearchResult, lessonOptionId?: string | null) => {
    trackStartBooking({
      provider_type: 'tutor',
      provider_id: tutor.tutorId,
      booking_value: tutor.lessonOptions?.[0]?.price,
      currency: tutor.lessonOptions?.[0]?.lessonPriceCurrency ?? 'PLN',
    });
    setSelectedTutor(tutor);
    setSelectedLessonOptionId(lessonOptionId ?? null);
  };

  useEffect(() => {
    if (!organization) return;
    trackViewProvider({
      provider_type: 'space',
      provider_id: organization.id,
      provider_name: organization.name,
    });
  }, [organization?.id]);

  const roomData = useMemo(
    () => (organization ? flattenOrganizationRooms(organization) : null),
    [organization],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (organizationError || !organization || !roomData) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">
          {organizationError ?? t('orgPage.loadError', "Couldn't load organization details.")}
        </Alert>
      </Container>
    );
  }

  const primarySpace = spaces[0]?.space;
  const roomCount = roomData.roomListContext.rooms.length;
  const seoImage = primarySpace?.imageUrls?.[0] ?? organization.imageUrls?.[0];
  const seoDescription =
    primarySpace?.description?.trim() ||
    t('orgPage.seoDescription', 'Book lessons with tutors at {{name}}.', {
      name: organization.name,
    });

  return (
    <>
      <SEO
        title={t('orgPage.seoTitle', '{{name}} tutors', { name: organization.name })}
        description={seoDescription}
        image={seoImage}
        noindex={true}
      />
      <Container maxWidth={false} sx={{ maxWidth: 1120, py: { xs: 1.5, md: 2 }, pb: { xs: 6, md: 8 } }}>
        <Stack spacing={1.5}>
          {spaces.map((spaceData, index) => (
            <PublicSpaceProfileHeader
              key={spaceData.space.id}
              space={spaceData.space}
              reviewStats={spaceData.reviewStats}
              spaceReviews={spaceData.reviews}
              spaceReviewsLoading={spaceData.reviewsLoading}
              titleComponent={index === 0 ? 'h1' : 'h2'}
              ratingName={`org-space-rating-${spaceData.space.id}`}
              href={publicSpacePagePath(spaceData.space.id)}
            />
          ))}

          {spaces.length === 0 ? (
            <Alert severity="info">
              {t('orgPage.noSpacesBody', 'This organization does not have public spaces available right now.')}
            </Alert>
          ) : null}

          <PublicTutorsRoomPanel
            labelsPrefix="orgPage"
            tutors={tutors}
            tutorError={tutorError}
            roomCount={roomCount}
            renderRooms={(viewMode) => (
              <PublicSpaceRoomList
                space={roomData.roomListContext}
                viewMode={viewMode}
                roomSpaceByRoomId={roomData.roomSpaceByRoomId}
              />
            )}
            tutorReviewsById={tutorReviewsById}
            tutorReviewsLoadingById={tutorReviewsLoadingById}
            loadTutorReviews={loadTutorReviews}
            onOpenTutorProfile={openTutorProfile}
            onOpenBooking={openBooking}
          />
        </Stack>
      </Container>

      <TutorBookingStepperDialog
        open={Boolean(selectedTutor)}
        tutor={selectedTutor}
        initialLessonOptionId={selectedLessonOptionId}
        onClose={() => {
          setSelectedTutor(null);
          setSelectedLessonOptionId(null);
        }}
      />
    </>
  );
};

export default OrgTutorsPageShell;
